<script>
/**
 * @typedef {import('../../../../asphaltbau-crm').GeocaptureVehicle} GeocaptureVehicle
 */

import PageLoading from "./../../../partials/PageLoading";
import KolonneSettingsVehiclesListItem from "./KolonneSettingsVehiclesListItem.vue";
import KolonneSettingsVehiclesRankingLegend from "./KolonneSettingsVehiclesRankingLegend.vue";

export default {
  name: "KolonneSettingsVehiclesModal",

  components: {
    PageLoading,
    KolonneSettingsVehiclesListItem,
    KolonneSettingsVehiclesRankingLegend,
  },

  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    week: {
      type: String | null,
      default: null,
    },
    startdate: {
      type: String, // format 'YYYY-MM-DD'
      default: '',
    },
    selectedKolonneId: {
      type: Number | String,
      default: ''
    },
    selectedKolonneNumber: {
      type: undefined | Number | String,
      default: ''
    },
    selectedVehicleIds: {
      type: Array,
      default: () => [],
    },
    availableVehicles: {
      type: Array | null,
      default: () => [],
    },
    kolonneContent: {
      type: Array,
      default: () => [],
    },
  },

  model: {
    prop: "showDialog",
    event: "showchange"
  },

  data() {
    return {
      isLoading: false,
      saveVehiclesError: undefined,

      isRefreshingData: false,
      refreshingDataError: undefined,

      vehicleSearchtext: "",
      selectedVehicles: [],
    };
  },

  mounted () {
    this.createSelectedVehicles();
  },

  computed: {
    showDialog: {
      get () {
        if (this.isVisible) {
          this.onOpenDialog();
        }
        return this.isVisible;
      },
      set (value) {
        this.$emit("showchange", value);
      }
    },

    filteredVehicles () {
      return this.availableVehicles
        .filter(vehicle => {
          const searchtext = this.vehicleSearchtext.toLowerCase()
          return vehicle.name.toLowerCase().includes(searchtext) ||
                  vehicle.categoryName?.toLowerCase().includes(searchtext);
        })
        .sort((a,b) => {
          return a.name.localeCompare(b.name);
        })
    }
  },

  methods: {
    createSelectedVehicles () {
      this.selectedVehicles = this.selectedVehicleIds
        .map(vehicleId => {
          const vehicle = this.availableVehicles.find(availableVehicle => {
            return availableVehicle.id === vehicleId
          })

          return {
            active: vehicle != null,
            ...(vehicle ?? {}),
          }
        })
        .sort((a,b) => a.name?.localeCompare(b.name));
    },

    /**
     * @param {GeocaptureVehicle} vehicle
     */
    isSelectedVehicle(vehicle) {
      return !!this.selectedVehicles.find(selectedVehicle => {
        return selectedVehicle.id === vehicle.id;
      });
    },

    onChangeVehicleSelected (vehicleId, isSelected) {
      isSelected
        ? this.selectVehicleById(vehicleId)
        : this.unselectVehicleById(vehicleId)
    },

    selectVehicleById (vehicleId) {
      const vehicle = this.availableVehicles.find(vehicle => vehicle.id === vehicleId);

      if (vehicle && !this.isSelectedVehicle(vehicle)) {
        this.selectedVehicles.push({
          active: true,
          ...vehicle,
        })
      }
    },

    unselectVehicleById (vehicleId) {
      this.selectedVehicles = this.selectedVehicles.filter(vehicle => {
        return vehicle.id !== vehicleId
      });

    },

    onOpenDialog () {
      this.showDialog = true;
    },

    onCloseDialog () {
      this.showDialog = false;
    },

    onSaveVehicles () {
      this.isLoading = true
      this.saveVehiclesError = undefined

      const selectedVehicleIds = this.selectedVehicles.flatMap(vehicle => vehicle.id);

      this.$api
        .clearParams()
        .put(`kolonne-settings/${this.selectedKolonneId}`, {
          fleets_ids: selectedVehicleIds,
        })
        .then(response => {
          window.app.$helpers.logger("Updating vehicles successful");
          this.$emit("saved", response.data.data);
        })
        .catch(error => {
          window.app.$helpers.logger('Failed to save vehicles', error);
          this.saveVehiclesError = error
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    onRefreshVehicleData () {
      this.isRefreshingData = true
      this.refreshingDataError = undefined

      this.$api
        .clearParams()
        .addParam('refresh', 1)
        .get('geocapture/vehicles')
        .then(response => {
          window.app.$helpers.logger("Refreshing vehicle data successful", {
            vehicles: response.data.data,
          });
          this.$emit("refreshedVehicles", response.data.data);
        })
        .catch(error => {
          window.app.$helpers.logger('Failed to refresh vehicle data', { error });
          this.refreshingDataError = error
        })
        .finally(() => {
          this.isRefreshingData = false;
        });
    },
  },
};
</script>

<template>
  <div class="dialog" :class="{ 'active' : showDialog }">
    <div class="dialog--content u-extra-medium-padding--bottom c-employee-modal">
      <h4 class="u-extra-small-margin--bottom">
        {{ $t('kolonne.vehicle.title.modal') }}

        <div v-show="isLoading">
          <div class="loading-spinner-wrapper">
            <PageLoading position="absolute" right/>
          </div>
        </div>
      </h4>

      <h5 class="u-color--primary u-extra-medium-margin--bottom">
        {{ $t('kolonne.title') }} {{ selectedKolonneNumber }}
        <template v-if="week"> — KW {{ week }}</template>
      </h5>

      <p v-if="selectedKolonneNumber == '' || selectedKolonneNumber == null" class="is-error">
        {{ $t('kolonne.error.nokolonne') }}
      </p>

      <div v-else>
        <div class="grid-container fluid" v-if="saveVehiclesError">
          <div class="grid-x grid-margin-x align-center align-middle">
            <div class="cell small">
              <div class="callout alert text-center">
                {{ $t('kolonne.vehicle.saveerror') }}: {{ saveVehiclesError.message }}
              </div>
            </div>
          </div>
        </div>

        <div class="grid-x grid-margin-x">
          <div class="cell small-6">
            <h6 class="u-color--dark-gray u-no-margin--bottom u-extra-small-padding--bottom">
              {{ $t('kolonne.vehicle.selected') }} ({{ selectedVehicles.length }})
            </h6>

            <p v-if="selectedVehicles.length === 0">
              {{ $t('kolonne.vehicle.novehicles.selected') }}
            </p>
          </div>
          <div class="cell small-6 text-right">
            <button class="button u-text-small" @click="onSaveVehicles">
              {{ $t('save') }}
            </button>
          </div>
        </div>

        <KolonneSettingsVehiclesListItem
          v-for="vehicle in selectedVehicles"
          :key="`selected-${vehicle.id}`"
          :active="true"
          :vehicle="vehicle"
          :startdate="startdate"
          :kolonne-content="kolonneContent"
          :hide-ranking="true"
          @activeChanged="onChangeVehicleSelected"
        />

        <p v-if="this.availableVehicles.length === 0" class="u-color--alert text-center h6">
          {{ $t('kolonne.message.novehicles') }}
        </p>

        <div v-else>
          <div class="grid-container fluid" v-if="refreshingDataError">
            <div class="grid-x grid-margin-x align-center align-middle">
              <div class="cell small">
                <div class="callout alert text-center">
                  {{ $t('kolonne.vehicle.refresherror') }}: {{ refreshingDataError.message }}
                </div>
              </div>
            </div>
          </div>

          <div class="grid-x grid-margin-x align-middle u-extra-medium-padding--top">
            <div class="cell small-6">
              <h6 class="u-color--dark-gray u-no-margin--bottom">
                {{ $t('kolonne.vehicle.available') }} ({{ availableVehicles.length }})
              </h6>
            </div>

            <div class="cell small-6">
              <div class="grid-x grid-margin-x align-middle">
                <div class="cell auto">
                  <!-- this is a spacer column to push the loading spinner to the right … -->
                </div>
                <div class="cell small-2 text-right">
                  <div class="loading-spinner-wrapper" v-if="isRefreshingData">
                    <PageLoading class="loading-spinner--small u-no-margin" />
                  </div>
                </div>
                <div class="cell shrink text-right">
                  <button
                    class="button button--small outline secondary"
                    :class="{'disabled': isRefreshingData }"
                    @click="onRefreshVehicleData"
                  >
                    {{ $t('kolonne.settings.data.refresh') }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <p class="u-text-medium u-extra-small-margin--bottom u-extra-small-margin--top">
            {{ $t('kolonne.vehicle.source.geocapture.hint') }}
          </p>

          <div class="grid-x grid-margin-x align-middle">
            <div class="cell small-12">
              <div class="form-group form-group--search u-extra-small-margin--bottom">
                <input
                  type="text"
                  name="vehiclefilter"
                  class="form-control form-control--small form-control--search"
                  :placeholder="$t('search')"
                  v-model="vehicleSearchtext" />
              </div>
            </div>
          </div>

          <KolonneSettingsVehiclesRankingLegend class="u-extra-medium-margin--bottom" />

          <div class="grid-x grid-margin-x align-middle">
            <KolonneSettingsVehiclesListItem
              v-for="vehicle in filteredVehicles"
              :key="vehicle.id"
              :active="isSelectedVehicle(vehicle)"
              :vehicle="vehicle"
              :startdate="startdate"
              :kolonne-content="kolonneContent"
              :selected-kolonne-id="selectedKolonneId"
              @activeChanged="onChangeVehicleSelected"
            />
          </div>
        </div>
      </div>

      <button class="close-button" @click.prevent="onCloseDialog">
          <span aria-hidden="true">&times;</span>
      </button>
    </div>
  </div>
</template>

